import { Description } from "@mui/icons-material";
import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import { useMemo } from "react";
import { isMobile } from "react-device-detect";

import { LargeContentBox, NoDataWarning } from "../../components/common";
import { PageHeader } from "../../components/common/PageHeader";
import { ExpandableStatusChip } from "../../components/common/prescriptionStack/ExpandableStatusChip";
import {
  PrescriptionMobileList,
  type PrescriptionStackItem,
} from "../../components/common/prescriptionStack/PrescriptionStackCarousel";
import { sanitizeSpecialty } from "../../components/common/prescriptionStack/prescriptionStackTheme";
import { ScreenMobile } from "../../components/common/ScreenMobile";
import { GenericTable } from "../../components/GenericTable/GenericTable";
import { usePatientHealthCareInformationContext } from "../../contexts/PatientHealthCareInformationContext";
import {
  findRelatedResources,
  extractOrganizationName,
  extractPosologyLines,
  isDocumentSigned,
  processDocumentBaseData,
} from "../../utils/fhirDocumentUtils";
import MedicalPrescriptionActions from "./components/MedicalPrescriptionActions";

interface IProps {
  open?: boolean;
  onClose?: VoidFunction;
}

const TABLE_COLUMNS = [
  { column: "Medicamentos", rowName: "description", lineClamp: 2 },
  { column: "Profissional", rowName: "practitioner" },
  { column: "Especialidade", rowName: "specialty" },
  { column: "Data", rowName: "date" },
  { column: "Assinatura", rowName: "signDisplay", align: "center" as const },
];

function buildPrescriptionRows(
  documentReferencePrescription: any[] | undefined,
  medicationRequestPrescription: any[] = [],
) {
  if (!documentReferencePrescription) return [];

  return documentReferencePrescription.map((el, index) => {
    const relatedMedications = findRelatedResources(
      el?.resource,
      medicationRequestPrescription,
    );
    const description =
      relatedMedications
        ?.map((m: any) => m?.resource?.medication?.concept?.text)
        ?.join(" , ") || "";
    const isSigned = isDocumentSigned(el?.resource);
    const baseData = processDocumentBaseData(el, isSigned);

    return {
      id: index,
      ...baseData,
      specialty: sanitizeSpecialty(baseData.specialty) ?? "-",
      description,
      isSigned,
      signDisplay: <ExpandableStatusChip signed={isSigned} />,
      medication: relatedMedications,
      sign: isSigned,
      organization: extractOrganizationName(el?.resource),
      posologyLines: extractPosologyLines(relatedMedications),
    };
  });
}

function PrescriptionListContent({
  rows,
  medicationRequest,
}: {
  rows: ReturnType<typeof buildPrescriptionRows>;
  medicationRequest?: any[];
}) {
  if (rows.length === 0) {
    return <NoDataWarning message="Nenhuma receita médica encontrada." />;
  }

  if (isMobile) {
    const items: PrescriptionStackItem[] = rows.map((row) => ({
      id: row.id,
      description: row.description,
      date: row.date,
      practitioner: row.practitioner,
      specialty: row.specialty !== "-" ? row.specialty : undefined,
      sign: row.isSigned,
      binaryUrl: row.binaryUrl,
      fullResource: row.fullResource,
      organization: row.organization,
      posologyLines: row.posologyLines,
    }));

    return (
      <PrescriptionMobileList
        items={items}
        medicationRequest={medicationRequest ?? []}
        renderActions={(item) => {
          const fullRow = rows.find((r) => r.id === item.id) ?? rows[0];
          return <MedicalPrescriptionActions row={fullRow} cardActions />;
        }}
      />
    );
  }

  return (
    <GenericTable
      tableColumns={TABLE_COLUMNS}
      tableRows={rows}
      actions={(row) => <MedicalPrescriptionActions row={row} />}
    />
  );
}

export default function MedicalPrescription({ open, onClose }: IProps) {
  const { documentReferencePrescription, medicationRequestPrescription } =
    usePatientHealthCareInformationContext();

  const rows = useMemo(
    () =>
      buildPrescriptionRows(
        documentReferencePrescription,
        medicationRequestPrescription ?? [],
      ),
    [documentReferencePrescription, medicationRequestPrescription],
  );

  const isLoading = documentReferencePrescription === undefined;
  const isModal = open !== undefined;

  const listBody = isLoading ? (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        py: 6,
      }}
    >
      <CircularProgress size={40} color="primary" sx={{ mb: 2 }} />
      <Typography variant="body1" color="neutral700.main">
        Carregando receitas...
      </Typography>
    </Box>
  ) : (
    <PrescriptionListContent
      rows={rows}
      medicationRequest={medicationRequestPrescription ?? []}
    />
  );

  if (isMobile && isModal) {
    return (
      <ScreenMobile
        title="Receitas médicas"
        open={open ?? false}
        onClose={onClose}
      >
        <Stack width="100%" sx={{ px: 0.5, py: 1 }}>
          {listBody}
        </Stack>
      </ScreenMobile>
    );
  }

  if (isMobile) {
    return (
      <Stack spacing={2} sx={{ width: "100%", px: 1, py: 1 }}>
        <PageHeader
          title="Receitas médicas"
          icon={Description}
          description="Visualize e baixe suas receitas médicas"
        />
        {listBody}
      </Stack>
    );
  }

  return (
    <LargeContentBox>
      <Stack width="100%" spacing={2}>
        <PageHeader
          title="Receitas médicas"
          icon={Description}
          description="Visualize, baixe e acompanhe o uso dos seus medicamentos"
        />
        {listBody}
      </Stack>
    </LargeContentBox>
  );
}
